import { useEffect, useRef, useState } from "react";
import { PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AddOrganisationMembershipDialog } from "@/components/AddOrganisationMembershipDialog";
import { EditUserRoleDialog } from "@/components/EditUserRoleDialog";
import { PaginationBar } from "@/components/PaginationBar";
import { RemoveOrganisationMembershipDialog } from "@/components/RemoveOrganisationMembershipDialog";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDateTime } from "@/lib/format";
import { Link } from "react-router-dom";

type OrganisationRole = components["schemas"]["OrganisationRole"];
type OrganisationUser = components["schemas"]["OrganisationUserResponse"];
type User = OrganisationUser | components["schemas"]["UserResponse"];

interface UsersTableProps {
  organisationId?: string;
  organisationName?: string;
}

export function UsersTable({ organisationId, organisationName }: UsersTableProps) {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput);

  const pagination = useCursorPagination({ initialLimit: 20 });

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<OrganisationUser | null>(null);
  const [removing, setRemoving] = useState<OrganisationUser | null>(null);
  const [adding, setAdding] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const prevSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      pagination.reset();
    }
  }, [debouncedSearch, pagination.reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchUsers() {
      setLoading(true);
      setError(null);
      const query = {
        Email: debouncedSearch || undefined,
        Cursor: pagination.cursor,
        Limit: pagination.limit,
      };
      const result = organisationId
        ? await api.GET("/admin/organisations/{organisationId}/users", {
            params: { path: { organisationId }, query },
          })
        : await api.GET("/admin/users", { params: { query } });
      if (cancelled) return;
      if (result.error) {
        setError("Failed to load users.");
      } else {
        setUsers(result.data?.data ?? []);
        pagination.setPage({
          nextCursor: result.data?.meta?.cursors?.next,
          previousCursor: result.data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [organisationId, pagination.cursor, pagination.limit, debouncedSearch, refreshKey]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  const showRole = !!organisationId;
  const canEditRole = !!organisationId;
  const colCount = 3 + (showRole ? 1 : 0) + 1 + (canEditRole ? 1 : 0); // name, email, id, [role], created, [actions]

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Input
          placeholder="Filter by email..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-sm"
        />
        {loading && <Spinner className="size-5" />}
        {organisationId && (
          <Button className="ml-auto" size="sm" onClick={() => setAdding(true)}>
            <PlusIcon className="size-4" />
            Add user
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>ID</TableHead>
              {showRole && <TableHead>Role</TableHead>}
              <TableHead>Created</TableHead>
              {canEditRole && <TableHead className="w-[100px]" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 && !loading ? (
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  className="text-muted-foreground h-24 text-center"
                >
                  {debouncedSearch
                    ? `No users matching "${debouncedSearch}".`
                    : "No users found."}
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    {user.name || <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Link to={`/users/${user.id}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {user.id}
                      </Badge>
                    </Link>
                  </TableCell>
                  {"role" in user && (
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {user.role}
                      </Badge>
                    </TableCell>
                  )}
                  <TableCell>{formatDateTime(user.createdAt)}</TableCell>
                  {canEditRole && (
                    <TableCell>
                      {"role" in user && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            onClick={() => setEditing(user)}
                            aria-label="Edit role"
                          >
                            <PencilIcon className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive size-8"
                            onClick={() => setRemoving(user)}
                            aria-label="Remove from organisation"
                          >
                            <Trash2Icon className="size-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />

      {organisationId && (
        <AddOrganisationMembershipDialog
          open={adding}
          onOpenChange={setAdding}
          organisationId={organisationId}
          organisationName={organisationName}
          onAdded={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {organisationId && removing && removing.id && (
        <RemoveOrganisationMembershipDialog
          open={removing !== null}
          onOpenChange={(open) => !open && setRemoving(null)}
          userId={removing.id}
          userLabel={removing.name || removing.email || removing.id}
          organisationId={organisationId}
          organisationName={organisationName ?? "this organisation"}
          onRemoved={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {organisationId && editing && editing.id && (
        <EditUserRoleDialog
          open={editing !== null}
          onOpenChange={(open) => !open && setEditing(null)}
          userId={editing.id}
          organisationId={organisationId}
          organisationName={organisationName ?? "this organisation"}
          currentRole={editing.role}
          onUpdated={(newRole) => {
            setUsers((prev) =>
              prev.map((u) =>
                u.id === editing.id ? { ...u, role: newRole } : u,
              ),
            );
          }}
        />
      )}
    </div>
  );
}
